import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Spinner, Alert, Badge, Form, InputGroup,
  Pagination, Dropdown, Modal,
} from 'react-bootstrap';
import inboundApi from '../../config/inboundApi';
import CreateStaffModal from './components/CreateStaffModal';

const ROLE_COLORS = { admin: 'danger', agent: 'primary', contributor: 'secondary' };

export default function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: '', status: '' });
  const [editSaving, setEditSaving] = useState(false);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await inboundApi.get('/staff', { params });
      setStaff(res.data.staff);
      setTotal(res.data.total);
    } catch {
      setMessage({ type: 'danger', text: 'Failed to load team members' });
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      await inboundApi.delete(`/staff/${id}`);
      setMessage({ type: 'success', text: 'Team member removed' });
      fetchStaff();
    } catch {
      setMessage({ type: 'danger', text: 'Failed to remove team member' });
    }
  };

  const openEdit = (s) => {
    setEditItem(s);
    setEditForm({ name: s.name, role: s.role, status: s.status });
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await inboundApi.put(`/staff/${editItem.id}`, editForm);
      setEditItem(null);
      setMessage({ type: 'success', text: 'Team member updated' });
      fetchStaff();
    } catch {
      setMessage({ type: 'danger', text: 'Failed to update team member' });
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreateClose = (saved) => {
    setShowCreate(false);
    if (saved) {
      setMessage({ type: 'success', text: 'Team member created' });
      fetchStaff();
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const getInitial = (name) => (name || '?').charAt(0).toUpperCase();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
        <div className="d-flex gap-3">
          <InputGroup style={{ maxWidth: 300 }}>
            <Form.Control
              placeholder="Search team members..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </InputGroup>
          <Form.Select
            style={{ maxWidth: 160 }}
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="agent">Agent</option>
            <option value="contributor">Contributor</option>
          </Form.Select>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          Create team member
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : staff.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No team members yet. Click "Create team member" to add one.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Groups</th>
                  <th>Role</th>
                  <th>Last Sign-In</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          backgroundColor: '#e9ecef', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontWeight: 600, fontSize: '0.85rem', flexShrink: 0,
                        }}>
                          {getInitial(s.name)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{s.name}</div>
                          <small className="text-muted">{s.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {(s.groups || []).slice(0, 2).map((g) => (
                        <Badge key={g.id} bg="light" text="dark" className="me-1">
                          {g.name}
                        </Badge>
                      ))}
                      {(s.groups || []).length > 2 && (
                        <Badge bg="light" text="dark">+{s.groups.length - 2} more</Badge>
                      )}
                    </td>
                    <td>
                      <Badge bg={ROLE_COLORS[s.role] || 'secondary'}>
                        {s.role}
                      </Badge>
                    </td>
                    <td>
                      <small className="text-muted">
                        {s.last_sign_in
                          ? new Date(s.last_sign_in).toLocaleDateString()
                          : 'Never'}
                      </small>
                    </td>
                    <td className="text-end">
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="link" size="sm" className="text-muted p-0" style={{ lineHeight: 1 }}>
                          &#x22EE;
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openEdit(s)}>Edit</Dropdown.Item>
                          <Dropdown.Item className="text-danger" onClick={() => handleDelete(s.id)}>Delete</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Showing {(page - 1) * pageSize + 1}&ndash;{Math.min(page * pageSize, total)} of {total}
          </small>
          <Pagination className="mb-0">
            <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const p = i + 1;
              return (
                <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
                  {p}
                </Pagination.Item>
              );
            })}
            {totalPages > 5 && <Pagination.Ellipsis disabled />}
            <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
          </Pagination>
        </div>
      )}

      <CreateStaffModal show={showCreate} onClose={handleCreateClose} />

      {/* Edit Modal */}
      <Modal show={!!editItem} onHide={() => setEditItem(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Team Member</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
            >
              <option value="admin">Admin</option>
              <option value="agent">Agent</option>
              <option value="contributor">Contributor</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={editForm.status}
              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditItem(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
